import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, CheckCircle2, XCircle, Trash2, FileText, ShoppingBag, MessageCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { REPORT_REASONS } from "./ReportDialog";
import { Button } from "./ui/button";

const REASON_LABEL = Object.fromEntries(REPORT_REASONS);
const TYPE_ICON = { post: FileText, listing: ShoppingBag, message: MessageCircle };
const FILTERS = [["open", "Open"], ["resolved", "Resolved"], ["dismissed", "Dismissed"], ["", "All"]];

function ReportRow({ r, onUpdate }) {
  const Icon = TYPE_ICON[r.target_type] || Flag;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`report-${r.report_id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded-full px-2.5 py-0.5 font-bold ${r.reason === "child_safety" ? "bg-rose-500 text-white" : "bg-rose-50 text-rose-600"}`}>{REASON_LABEL[r.reason] || r.reason}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2.5 py-0.5 font-semibold capitalize"><Icon className="h-3 w-3" /> {r.target_type}</span>
        <span className="text-slate-400">{new Date(r.created_at).toLocaleString()} · by {r.reporter_name}</span>
        <span className="ml-auto font-bold capitalize text-slate-500" data-testid={`report-status-${r.report_id}`}>{r.status}{r.content_removed ? " · removed" : ""}</span>
      </div>
      <div className="mt-3 flex gap-3">
        {r.snapshot?.image && <img src={r.snapshot.image} alt="" className="h-14 w-14 rounded-xl object-cover shrink-0" />}
        <div className="min-w-0 text-sm">
          <div className="font-bold text-slate-800">{r.snapshot?.author_name || "Unknown"}</div>
          <p className="text-slate-600 whitespace-pre-wrap break-words">{r.snapshot?.text}</p>
          {r.details && <p className="mt-1 text-xs text-slate-400">Reporter note: {r.details}</p>}
        </div>
      </div>
      {r.status === "open" && (
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" onClick={() => onUpdate(r, "resolved", true)} data-testid={`report-remove-${r.report_id}`} className="rounded-full bg-rose-500 hover:bg-rose-600 h-8"><Trash2 className="h-3.5 w-3.5 mr-1" /> Remove content</Button>
          <Button size="sm" variant="outline" onClick={() => onUpdate(r, "resolved", false)} data-testid={`report-resolve-${r.report_id}`} className="rounded-full h-8"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark resolved</Button>
          <Button size="sm" variant="ghost" onClick={() => onUpdate(r, "dismissed", false)} data-testid={`report-dismiss-${r.report_id}`} className="rounded-full h-8 text-slate-500"><XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss</Button>
        </div>
      )}
    </div>
  );
}

export default function AdminReports() {
  const [status, setStatus] = useState("open");
  const [reports, setReports] = useState([]);
  const load = useCallback(() => api.get("/admin/reports", { params: { status } }).then(({ data }) => setReports(data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);

  const update = async (r, next, remove) => {
    if (remove && !window.confirm("Permanently remove this content?")) return;
    try { await api.put(`/admin/reports/${r.report_id}`, { status: next, remove_content: remove }); toast.success(remove ? "Content removed" : `Report ${next}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-reports">
      <div className="inline-flex items-center rounded-full bg-slate-100 p-1 mb-4">
        {FILTERS.map(([v, l]) => (
          <button key={l} onClick={() => setStatus(v)} data-testid={`reports-filter-${l.toLowerCase()}`}
            className={`rounded-full px-4 py-2 text-sm font-bold ${status === v ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {reports.map((r) => <ReportRow key={r.report_id} r={r} onUpdate={update} />)}
        {reports.length === 0 && <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-400" data-testid="reports-empty">No reports here.</div>}
      </div>
    </div>
  );
}
